import React from 'react';
import { useIonRouter } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { BellIcon, InboxIcon, SettingsIcon } from './icons';
import { config } from '../Constants';

export type HeroChromeProps = {
  onMenuClick: () => void;
  msgCount?: number;
  notificationCount?: number;
  trailing?: React.ReactNode;
  showInbox?: boolean;
  showNotifications?: boolean;
  showSettings?: boolean;
  showProfile?: boolean;
  inboxTo?: string;
  notificationsTo?: string;
  settingsTo?: string;
};

function normalizePath(path: string): string {
  return path.replace(/\/+$/, '').toLowerCase() || '/';
}

function pathMatches(pathname: string, target: string): boolean {
  const current = normalizePath(pathname);
  const base = normalizePath(target);
  return current === base || current.startsWith(`${base}/`);
}

const HeroChrome: React.FC<HeroChromeProps> = ({
  onMenuClick,
  msgCount = 0,
  notificationCount = 0,
  trailing,
  showInbox = true,
  showNotifications = true,
  showSettings = true,
  inboxTo = '/inbox',
  notificationsTo = '/notifications',
  settingsTo = '/settings',
}) => {
  const ionRouter = useIonRouter();
  const location = useLocation();
  const iconColor = '#ffffff';

  const inboxActive = pathMatches(location.pathname, inboxTo);
  const notificationsActive = pathMatches(location.pathname, notificationsTo);
  const settingsActive = pathMatches(location.pathname, settingsTo);

  const go = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    ionRouter.push(path, 'forward', 'push');
  };

  return (
    <>
      {config.showSidenav ? (
        <button
          type="button"
          id="toolbar-image"
          className="home-hero__menu-tab"
          onClick={e => {
            e.stopPropagation();
            onMenuClick();
          }}
          aria-label="Open menu"
        >
          <span className="home-hero__hamburger" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>
      ) : null}

      <div className="home-hero__top">
        <div className="home-hero__actions">
          {showInbox ? (
            <a
              href={inboxTo}
              className={`home-hero__icon-btn${inboxActive ? ' is-active' : ''}`}
              aria-label="Inbox"
              id="HomeMessageLink"
              onClick={go(inboxTo)}
            >
              <span className="home-hero__icon-wrap">
                <InboxIcon size={25} color={iconColor} />
                {msgCount > 0 ? (
                  <span className="home-hero__badge">{msgCount > 99 ? '99+' : msgCount}</span>
                ) : null}
              </span>
            </a>
          ) : null}

          {showNotifications ? (
            <a
              href={notificationsTo}
              className={`home-hero__icon-btn${notificationsActive ? ' is-active' : ''}`}
              aria-label="Notifications"
              id="HomeNotificationLink"
              onClick={go(notificationsTo)}
            >
              <span className="home-hero__icon-wrap">
                <BellIcon size={25} color={iconColor} />
                {notificationCount > 0 ? (
                  <span className="home-hero__badge">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                ) : null}
              </span>
            </a>
          ) : null}

          {showSettings ? (
            <a
              href={settingsTo}
              id="HomeSettingLink"
              className={`home-hero__icon-btn${settingsActive ? ' is-active' : ''}`}
              aria-label="Settings"
              onClick={go(settingsTo)}
            >
              <SettingsIcon size={25} color={iconColor} />
            </a>
          ) : null}

          {trailing}
        </div>
      </div>
    </>
  );
};

export default HeroChrome;
